import { Link } from 'react-router-dom';
import styled from 'styled-components';

export const COMPANY = {
  name: '주식회사 오리넌',
  brand: 'Auto Threads',
  ceo: '김윤재',
  businessNumber: '488-86-03327',
  corporateNumber: '151111-0097331',
  address:
    '충청북도 청주시 청원구 안덕벌로104번길 28, 502호 청주대학교 미래창조관(내덕동)',
  phone: '010-9940-1841',
  email: 'orinan.develop@gmail.com',
} as const;

interface SiteFooterProps {
  /** 인증/법적 페이지용 좁은 폭 레이아웃 */
  $compact?: boolean;
  /** 앱 하단 탭 네비게이션 여백 (AppLayout에서만 true) */
  $withMobileNav?: boolean;
}

export function SiteFooter({
  $compact = false,
  $withMobileNav = false,
}: SiteFooterProps) {
  return (
    <Foot $compact={$compact} $withMobileNav={$withMobileNav}>
      <Inner $compact={$compact}>
        <BrandRow>
          <BrandName>{COMPANY.brand}</BrandName>
          <LegalNav>
            <LegalLink to="/privacy">개인정보 처리방침</LegalLink>
            <Sep aria-hidden>|</Sep>
            <LegalLink to="/data-deletion">데이터 삭제 안내</LegalLink>
          </LegalNav>
        </BrandRow>

        <Info>
          <span>
            <Label>상호</Label> {COMPANY.name}
          </span>
          <Sep aria-hidden>|</Sep>
          <span>
            <Label>대표</Label> {COMPANY.ceo}
          </span>
          <Sep aria-hidden>|</Sep>
          <span>
            <Label>사업자등록번호</Label> {COMPANY.businessNumber}
          </span>
          <Sep aria-hidden>|</Sep>
          <span>
            <Label>법인등록번호</Label> {COMPANY.corporateNumber}
          </span>
        </Info>

        <Address>
          <Label>주소</Label> {COMPANY.address}
        </Address>

        <Contact>
          <span>
            <Label>연락처</Label>{' '}
            <ContactLink href={`tel:${COMPANY.phone.replace(/-/g, '')}`}>
              {COMPANY.phone}
            </ContactLink>
          </span>
          <Sep aria-hidden>|</Sep>
          <ContactLink href={`mailto:${COMPANY.email}`}>{COMPANY.email}</ContactLink>
        </Contact>

        <Copy>
          © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
        </Copy>
      </Inner>
    </Foot>
  );
}

const Foot = styled.footer<{ $compact?: boolean; $withMobileNav?: boolean }>`
  flex-shrink: 0;
  width: 100%;
  margin-top: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ $compact }) => ($compact ? '20px 16px 28px' : '18px 24px 22px')};

  @media (max-width: 900px) {
    padding: 16px 16px
      ${({ $withMobileNav }) =>
        $withMobileNav
          ? 'calc(72px + env(safe-area-inset-bottom, 0px))'
          : '28px'};
  }
`;

const Inner = styled.div<{ $compact?: boolean }>`
  width: 100%;
  max-width: ${({ $compact }) => ($compact ? '760px' : '1280px')};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BrandName = styled.strong`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const LegalNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const LegalLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;
`;

const Sep = styled.span`
  margin: 0 8px;
  color: ${({ theme }) => theme.colors.borderStrong};

  @media (max-width: 768px) {
    display: none;
  }
`;

const Info = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 0;
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const Contact = styled.p`
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const Address = styled.p`
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Copy = styled.p`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
`;
